<template>
  <div class="hm-page">
    <!-- Header -->
    <div class="hm-header">
      <div class="hm-header-left">
        <h2 class="hm-title">Mapa de Calor</h2>
        <p class="hm-subtitle">Veja onde os visitantes clicam, rolam e como se comportam nas páginas dos seus vídeos</p>
      </div>
      <div class="hm-header-right">
        <div class="hm-date-group">
          <label>De</label>
          <input type="date" v-model="dateStart" class="hm-input-date" @change="loadData" />
          <label>Até</label>
          <input type="date" v-model="dateEnd" class="hm-input-date" @change="loadData" />
        </div>
      </div>
    </div>

    <!-- VSL selector -->
    <div class="hm-vsls">
      <div
        v-for="vsl in vsls"
        :key="vsl.id_vsl"
        class="hm-vsl-card"
        :class="{ active: selectedVsl && selectedVsl.id_vsl === vsl.id_vsl }"
        @click="selectVsl(vsl)"
      >
        <div class="hm-vsl-name">{{ vsl.vsl_name || 'Sem nome' }}</div>
        <div class="hm-vsl-stats">
          <span class="hm-badge click">{{ vsl.total_clicks }} cliques</span>
          <span class="hm-badge scroll">{{ vsl.total_scrolls }} sessões</span>
        </div>
      </div>
      <div v-if="!vsls.length && !loading" class="hm-empty">
        <span>Nenhum dado de mapa de calor ainda.</span>
        <p>Os dados aparecem automaticamente quando visitantes acessarem páginas com seu player.</p>
      </div>
    </div>

    <!-- Main view -->
    <div v-if="selectedVsl" class="hm-main">

      <!-- Tabs -->
      <div class="hm-tabs">
        <button class="hm-tab" :class="{ active: tab === 'scroll' }" @click="switchTab('scroll')">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12l7 7 7-7"/></svg>
          Profundidade de Scroll
        </button>
        <button class="hm-tab" :class="{ active: tab === 'behavior' }" @click="switchTab('behavior')">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>
          Comportamento
          <span class="hm-tab-badge">Novo</span>
        </button>
      </div>

      <!-- Scroll depth funnel -->
      <div v-if="tab === 'scroll'" class="hm-scroll-wrap">
        <div class="hm-scroll-header">
          Profundidade de Scroll — quantos visitantes chegaram até cada ponto da página
        </div>
        <div class="hm-scroll-chart">
          <div v-for="bucket in scrollBuckets" :key="bucket.depth_pct" class="hm-scroll-row">
            <div class="hm-scroll-label">{{ bucket.depth_pct }}%</div>
            <div class="hm-scroll-bar-wrap">
              <div
                class="hm-scroll-bar"
                :style="{ width: (bucket.pct_of_max * 100) + '%', background: scrollBarColor(bucket.pct_of_max) }"
              ></div>
              <span class="hm-scroll-count">{{ bucket.total }} sessões</span>
            </div>
          </div>
          <div v-if="!scrollBuckets.length && !loadingScroll" class="hm-empty">
            Sem dados de scroll no período selecionado
          </div>
        </div>
      </div>

      <!-- Behavior tab -->
      <div v-if="tab === 'behavior'" class="hm-beh-wrap">

        <!-- Behavior event cards -->
        <div class="hm-beh-section">
          <h3 class="hm-beh-section-title">Eventos Comportamentais</h3>
          <div class="hm-beh-cards">
            <div v-for="card in behaviorCards" :key="card.key" class="hm-beh-card">
              <div class="hm-beh-card-icon" :style="{ background: card.bg }">
                <span v-html="card.icon"></span>
              </div>
              <div class="hm-beh-card-info">
                <div class="hm-beh-card-value">{{ behCount(card.key) }}</div>
                <div class="hm-beh-card-label">{{ card.label }}</div>
                <div class="hm-beh-card-desc">{{ card.desc }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Cluster distribution -->
        <div class="hm-beh-section">
          <h3 class="hm-beh-section-title">Perfis de Visitante</h3>
          <p class="hm-beh-section-sub">{{ totalSessions }} sessões analisadas no período</p>
          <div v-if="sessions.length" class="hm-clusters">
            <div v-for="c in clustersSorted" :key="c.cluster" class="hm-cluster-row">
              <div class="hm-cluster-dot" :style="{ background: clusterColor(c.cluster) }"></div>
              <div class="hm-cluster-name">{{ c.cluster }}</div>
              <div class="hm-cluster-bar-wrap">
                <div
                  class="hm-cluster-bar"
                  :style="{ width: clusterPct(c) + '%', background: clusterColor(c.cluster) }"
                ></div>
              </div>
              <div class="hm-cluster-pct">{{ clusterPct(c) }}%</div>
              <div class="hm-cluster-n">{{ c.total }} sess.</div>
            </div>
          </div>
          <div v-else-if="!loadingBehavior" class="hm-empty">
            Sem sessões registradas no período.<br>
            <small>Os perfis aparecem após visitantes navegarem com o novo player ativo.</small>
          </div>
        </div>

        <!-- Cluster descriptions -->
        <div v-if="sessions.length" class="hm-beh-section">
          <h3 class="hm-beh-section-title">O que cada perfil significa</h3>
          <div class="hm-cluster-defs">
            <div v-for="def in clusterDefs" :key="def.name" class="hm-cluster-def">
              <div class="hm-cluster-def-badge" :style="{ background: clusterColor(def.name) + '22', color: clusterColor(def.name), borderColor: clusterColor(def.name) + '44' }">{{ def.name }}</div>
              <div class="hm-cluster-def-text">{{ def.desc }}</div>
            </div>
          </div>
        </div>

        <!-- Rule-based insights -->
        <div v-if="insights.length" class="hm-beh-section">
          <h3 class="hm-beh-section-title">Insights Automáticos</h3>
          <div class="hm-insights">
            <div v-for="(ins, i) in insights" :key="i" class="hm-insight" :class="ins.type">
              <div class="hm-insight-icon">{{ ins.icon }}</div>
              <div class="hm-insight-text">{{ ins.text }}</div>
            </div>
          </div>
        </div>

        <!-- Claude AI Insights -->
        <div v-if="sessions.length" class="hm-beh-section">
          <div class="hm-ai-header">
            <h3 class="hm-beh-section-title">Análise de IA</h3>
            <button class="hm-ai-btn" :disabled="loadingAi" @click="loadAiInsights">
              <svg v-if="!loadingAi" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
              <span v-if="loadingAi" class="hm-ai-spin"></span>
              {{ loadingAi ? 'Analisando...' : (aiInsights ? 'Regenerar' : 'Analisar com IA') }}
            </button>
          </div>
          <div v-if="aiInsights" class="hm-ai-result">{{ aiInsights }}</div>
          <div v-else-if="!loadingAi" class="hm-ai-placeholder">
            Clique em "Analisar com IA" para receber uma análise personalizada dos dados comportamentais desta VSL.
          </div>
        </div>

        <div v-if="loadingBehavior" class="hm-empty">Carregando dados comportamentais...</div>
      </div>

    </div>

    <!-- Loading overlay -->
    <div v-if="loading" class="hm-loading-overlay">
      <div class="hm-spinner"></div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

const API = process.env.VUE_APP_API_URL || '/api/dkbackend'

const CLUSTER_COLORS = {
  Perdido:     '#ef4444',
  Decidido:    '#f59e0b',
  Scanner:     '#8b5cf6',
  Pesquisador: '#3b82f6',
  Engajado:    '#10b981',
  Neutro:      '#64748b',
}

const CLUSTER_DEFS = [
  { name: 'Engajado',    desc: 'Visitante atento: ficou em elementos específicos por vários segundos e clicou com intenção. Alta probabilidade de conversão.' },
  { name: 'Pesquisador', desc: 'Rolou fundo na página, explorou com cuidado. Está avaliando antes de decidir — conteúdo denso funciona bem para este perfil.' },
  { name: 'Decidido',    desc: 'Clicou várias vezes na mesma área (rage click). Geralmente significa que tentou agir mas encontrou algum obstáculo no caminho.' },
  { name: 'Scanner',     desc: 'Subiu e desceu a página várias vezes, sem parar muito em nenhum elemento. Está procurando algo específico.' },
  { name: 'Neutro',      desc: 'Comportamento misto: rolou um pouco, clicou pouco. Sem sinal forte de engajamento ou desistência.' },
  { name: 'Perdido',     desc: 'Saiu cedo ou moveu o cursor para fechar a aba. Não encontrou o que esperava rapidamente.' },
]

export default {
  name: 'HeatmapIndex',

  data() {
    const today = new Date()
    const thirtyDaysAgo = new Date(today)
    thirtyDaysAgo.setDate(today.getDate() - 30)
    return {
      dateStart: thirtyDaysAgo.toISOString().slice(0, 10),
      dateEnd:   today.toISOString().slice(0, 10),
      vsls:         [],
      selectedVsl:  null,
      tab:          'scroll',
      clicks:       [],
      scrollData:   [],
      sessions:     [],
      behaviorData: [],
      loading:      false,
      loadingClicks:   false,
      loadingScroll:   false,
      loadingBehavior: false,
      loadingAi:       false,
      aiInsights:      '',
      pageUrl:         '',
      pageUrlSource:   null,
      iframeBlocked:   false,
      avgViewportW:    0,
      avgViewportH:    0,
      maxScrollPct:    0,
      iframePageRatio: 0,
      behaviorCards: [
        { key: 'rage_click',        label: 'Rage Clicks',       icon: '👊', bg: 'rgba(239,68,68,.15)',   desc: '3+ cliques no mesmo lugar em 1s' },
        { key: 'hover_dwell',       label: 'Hover Dwell',       icon: '🖱️', bg: 'rgba(59,130,246,.15)',  desc: 'Mouse parado ≥500ms' },
        { key: 'exit_intent',       label: 'Exit Intent',       icon: '🚪', bg: 'rgba(245,158,11,.15)',  desc: 'Cursor em direção ao fechar' },
        { key: 'scroll_reversal',   label: 'Scroll Reversal',   icon: '↕️', bg: 'rgba(139,92,246,.15)',  desc: 'Mudança de direção no scroll' },
        { key: 'visibility_change', label: 'Troca de Aba',      icon: '👁️', bg: 'rgba(16,185,129,.15)',  desc: 'Visitante saiu e voltou' },
      ],
      clusterDefs: CLUSTER_DEFS,
    }
  },

  computed: {
    userId() {
      try {
        const u = JSON.parse(localStorage.getItem('userData') || '{}')
        return u.id || this.$store?.state?.AppActiveUser?.id || null
      } catch { return null }
    },
    totalClicks() {
      return this.clicks.reduce((s, c) => s + Number(c.total), 0)
    },
    scrollBuckets() {
      if (!this.scrollData.length) return []
      const max = Math.max(...this.scrollData.map(r => Number(r.total)), 1)
      return this.scrollData.map(r => ({ ...r, pct_of_max: Number(r.total) / max }))
    },
    totalSessions() {
      return this.sessions.reduce((s, c) => s + Number(c.total), 0)
    },
    clustersSorted() {
      return [...this.sessions].sort((a, b) => Number(b.total) - Number(a.total))
    },
    insights() {
      if (!this.sessions.length && !this.behaviorData.length) return []
      const tips = []
      const total = this.totalSessions

      const find = (name) => this.sessions.find(s => s.cluster === name)
      const pct  = (s) => total > 0 ? Math.round((Number(s?.total || 0) / total) * 100) : 0

      const perdidos    = pct(find('Perdido'))
      const decididos   = pct(find('Decidido'))
      const engajados   = pct(find('Engajado'))
      const pesquisadores = pct(find('Pesquisador'))
      const scanners    = pct(find('Scanner'))
      const rageCount   = this.behCount('rage_click')
      const exitCount   = this.behCount('exit_intent')

      if (perdidos > 50)
        tips.push({ type: 'warn', icon: '⚠️', text: `${perdidos}% dos visitantes saem sem engajar. Considere melhorar o início do vídeo ou a velocidade de carregamento da página.` })
      if (decididos > 15 && rageCount > 5)
        tips.push({ type: 'warn', icon: '🔴', text: `${rageCount} rage clicks detectados — ${decididos}% das sessões têm visitantes tentando clicar em algo sem resposta. Verifique se há CTAs quebrados.` })
      if (exitCount > total * 0.3)
        tips.push({ type: 'warn', icon: '🚪', text: `Alta intenção de saída: ${exitCount} eventos de exit intent. Considere um popup de retenção ou oferta de saída.` })
      if (engajados > 30)
        tips.push({ type: 'ok', icon: '✅', text: `${engajados}% das sessões são Engajadas — ótimo sinal. Certifique-se de que seu CTA aparece após os momentos de maior atenção.` })
      if (pesquisadores > 20)
        tips.push({ type: 'ok', icon: '🔍', text: `${pesquisadores}% são Pesquisadores (leram fundo). Este público responde bem a provas sociais e comparações no final da página.` })
      if (scanners > 25)
        tips.push({ type: 'info', icon: '↕️', text: `${scanners}% são Scanners — visitantes que não encontraram o que buscavam rapidamente. Adicione âncoras visuais ou um sumário clicável.` })

      return tips
    },
  },

  watch: {
    tab(val) {
      if (val === 'scroll')   this.loadScrollData()
      else if (val === 'behavior') this.loadBehaviorData()
    },
  },

  mounted() {
    this.loadSummary()
  },

  methods: {
    switchTab(t) { this.tab = t },

    async loadData() {
      await this.loadSummary()
      if (this.selectedVsl) {
        if (this.tab === 'scroll')   this.loadScrollData()
        else if (this.tab === 'behavior') this.loadBehaviorData()
      }
    },

    async loadSummary() {
      if (!this.userId) return
      this.loading = true
      try {
        const { data } = await axios.get(
          `${API}/heatmap/summary/${this.userId}`,
          { params: { start: this.dateStart, end: this.dateEnd } }
        )
        this.vsls = data
        if (this.vsls.length && !this.selectedVsl) this.selectVsl(this.vsls[0])
      } catch (e) {
        console.error('heatmap summary error', e)
      } finally {
        this.loading = false
      }
    },

    async loadAiInsights() {
      if (!this.selectedVsl) return
      this.loadingAi = true
      try {
        const { data } = await axios.get(
          `${API}/heatmap/ai-insights/${this.selectedVsl.id_vsl}`,
          { params: { start: this.dateStart, end: this.dateEnd } }
        )
        this.aiInsights = data.insights || ''
      } catch (e) {
        console.error('ai insights error', e)
        this.aiInsights = 'Não foi possível gerar a análise. Tente novamente.'
      } finally {
        this.loadingAi = false
      }
    },

    async selectVsl(vsl) {
      this.selectedVsl = vsl
      this.clicks = []; this.scrollData = []; this.sessions = []; this.behaviorData = []
      this.aiInsights = ''; this.pageUrl = ''; this.iframeBlocked = false
      await this.loadPageUrl()
      if (this.tab === 'scroll')   await this.loadScrollData()
      else if (this.tab === 'behavior') await this.loadBehaviorData()
    },

    async loadPageUrl() {
      if (!this.selectedVsl) return
      try {
        const { data } = await axios.get(`${API}/heatmap/page-url/${this.selectedVsl.id_vsl}`)
        this.pageUrl = data.page_url || ''
        this.pageUrlSource = data.source || null
        // Backend verified: site blocks iframe embedding
        if (data.page_url && data.frameable === false) {
          this.iframeBlocked = true
        }
      } catch { /* silencioso */ }
    },

    onIframeLoad() {
      // Quando o iframe for same-origin conseguimos medir a altura real da página
      // e assim renderizar o heatmap em toda a extensão do documento.
      try {
        const iframe = this.$refs.bgIframe
        const doc = iframe && iframe.contentDocument
        if (doc && doc.documentElement) {
          const el = doc.documentElement
          const body = doc.body
          const docH = Math.max(
            el.scrollHeight || 0,
            el.offsetHeight || 0,
            body ? body.scrollHeight : 0,
            body ? body.offsetHeight : 0
          )
          const docW = Math.max(
            el.scrollWidth || 0,
            el.clientWidth || 0,
            body ? body.scrollWidth : 0,
            body ? body.clientWidth : 0
          )
          if (docH > 0 && docW > 0) this.iframePageRatio = docH / docW
        }
      } catch (_) {
        // Cross-origin: mantém fallback por viewport médio.
      }
      this.renderHeatmap()
    },

    async loadViewportStats() {
      if (!this.selectedVsl) return
      try {
        const { data } = await axios.get(
          `${API}/heatmap/viewport/${this.selectedVsl.id_vsl}`,
          { params: { start: this.dateStart, end: this.dateEnd } }
        )
        this.avgViewportW = Number(data.avg_viewport_w) || 0
        this.avgViewportH = Number(data.avg_viewport_h) || 0
        this.maxScrollPct = Number(data.max_scroll_pct) || 0
      } catch (_) {
        this.avgViewportW = 0
        this.avgViewportH = 0
        this.maxScrollPct = 0
      }
    },

    async loadClicks() {
      if (!this.selectedVsl) return
      this.loadingClicks = true
      try {
        const params = { start: this.dateStart, end: this.dateEnd }
        const [clickResp] = await Promise.all([
          axios.get(`${API}/heatmap/clicks/${this.selectedVsl.id_vsl}`, { params }),
          this.loadViewportStats(),
        ])
        const { data } = clickResp
        this.clicks = data
        this.$nextTick(() => this.renderHeatmap())
      } catch (e) { console.error('heatmap clicks error', e) }
      finally { this.loadingClicks = false }
    },

    async loadScrollData() {
      if (!this.selectedVsl) return
      this.loadingScroll = true
      try {
        const { data } = await axios.get(
          `${API}/heatmap/scroll/${this.selectedVsl.id_vsl}`,
          { params: { start: this.dateStart, end: this.dateEnd } }
        )
        this.scrollData = data
      } catch (e) { console.error('heatmap scroll error', e) }
      finally { this.loadingScroll = false }
    },

    async loadBehaviorData() {
      if (!this.selectedVsl) return
      this.loadingBehavior = true
      try {
        const params = { start: this.dateStart, end: this.dateEnd }
        const [beh, sess] = await Promise.all([
          axios.get(`${API}/heatmap/behavior/${this.selectedVsl.id_vsl}`, { params }).then(r => r.data),
          axios.get(`${API}/heatmap/sessions/${this.selectedVsl.id_vsl}`, { params }).then(r => r.data),
        ])
        this.behaviorData = beh
        this.sessions = sess
      } catch (e) { console.error('heatmap behavior error', e) }
      finally { this.loadingBehavior = false }
    },

    behCount(key) {
      const row = this.behaviorData.find(r => r.event_type === key)
      return row ? Number(row.total) : 0
    },

    clusterColor(name) { return CLUSTER_COLORS[name] || '#64748b' },

    clusterPct(c) {
      return this.totalSessions > 0
        ? Math.round((Number(c.total) / this.totalSessions) * 100)
        : 0
    },

    /* ── Canvas heatmap renderer ─────────────────────────────── */
    renderHeatmap() {
      const canvas = this.$refs.heatmapCanvas
      if (!canvas) return
      const W = this.$refs.canvasContainer?.clientWidth || 800
      const H = this.resolveCanvasHeight(W)
      canvas.width = W; canvas.height = H
      const ctx = canvas.getContext('2d')

      const hasBg = !!(this.pageUrl && !this.iframeBlocked)
      ctx.clearRect(0, 0, W, H)
      if (!hasBg) {
        ctx.fillStyle = '#1a1a2e'
        ctx.fillRect(0, 0, W, H)
        ctx.strokeStyle = 'rgba(255,255,255,0.04)'
        ctx.lineWidth = 1
        for (let i = 0; i <= 10; i++) {
          const y = (i / 10) * H
          ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke()
          const x = (i / 10) * W
          ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke()
        }
      }
      if (!this.clicks.length) return

      const maxTotal = Math.max(...this.clicks.map(c => Number(c.total)), 1)
      const RADIUS = Math.max(20, Math.round(W * 0.06))

      if (hasBg) {
        // Transparent mode: colored RGBA blobs directly over iframe
        for (const pt of this.clicks) {
          const x = (pt.x_pct / 100) * W
          const y = (pt.y_pct / 100) * H
          const intensity = Number(pt.total) / maxTotal
          const [r, g, b] = this._heatColor(intensity)
          const alpha = Math.min(0.65, 0.12 + intensity * 0.53)
          const grad = ctx.createRadialGradient(x, y, 0, x, y, RADIUS)
          grad.addColorStop(0,   `rgba(${r},${g},${b},${alpha})`)
          grad.addColorStop(0.5, `rgba(${r},${g},${b},${alpha * 0.5})`)
          grad.addColorStop(1,   `rgba(${r},${g},${b},0)`)
          ctx.fillStyle = grad
          ctx.fillRect(x - RADIUS, y - RADIUS, RADIUS * 2, RADIUS * 2)
        }
        // Depth labels
        ctx.fillStyle = 'rgba(0,0,0,0.35)'
        ctx.font = `${Math.max(10, Math.round(W * 0.015))}px sans-serif`
        for (let i = 1; i <= 10; i++) {
          const ly = (i / 10) * H
          ctx.fillStyle = 'rgba(0,0,0,0.3)'
          ctx.fillRect(0, ly - 1, W, 1)
          ctx.fillStyle = 'rgba(0,0,0,0.5)'
          ctx.fillText(`${i * 10}%`, 4, ly - 3)
        }
      } else {
        // Dark mode: off-canvas luminance → color mapping
        const off = document.createElement('canvas')
        off.width = W; off.height = H
        const octx = off.getContext('2d')
        octx.globalCompositeOperation = 'screen'

        for (const pt of this.clicks) {
          const x = (pt.x_pct / 100) * W
          const y = (pt.y_pct / 100) * H
          const intensity = Number(pt.total) / maxTotal
          const alpha = Math.min(0.9, 0.15 + intensity * 0.75)
          const grad = octx.createRadialGradient(x, y, 0, x, y, RADIUS)
          grad.addColorStop(0, `rgba(255,255,255,${alpha})`)
          grad.addColorStop(0.4, `rgba(255,255,255,${alpha * 0.4})`)
          grad.addColorStop(1, 'rgba(0,0,0,0)')
          octx.fillStyle = grad
          octx.fillRect(x - RADIUS, y - RADIUS, RADIUS * 2, RADIUS * 2)
        }

        const imgData = octx.getImageData(0, 0, W, H)
        const d = imgData.data
        for (let i = 0; i < d.length; i += 4) {
          const lum = d[i] / 255
          if (lum < 0.01) continue
          const [r, g, b] = this._heatColor(lum)
          d[i] = r; d[i+1] = g; d[i+2] = b; d[i+3] = Math.round(lum * 230)
        }
        octx.putImageData(imgData, 0, 0)
        ctx.drawImage(off, 0, 0)

        ctx.fillStyle = 'rgba(255,255,255,0.3)'
        ctx.font = `${Math.max(10, Math.round(W * 0.015))}px sans-serif`
        for (let i = 0; i <= 10; i++) {
          ctx.fillText(`${i * 10}%`, 4, (i / 10) * H - 3)
        }
      }
    },

    resolveCanvasHeight(width) {
      // 1) Melhor cenário: altura real da página medida no iframe (same-origin).
      if (this.iframePageRatio > 0) {
        return Math.max(1000, Math.round(width * this.iframePageRatio))
      }

      // 2) Fallback com viewport médio capturado nos eventos.
      let ratio = 1.4
      if (this.avgViewportW > 0 && this.avgViewportH > 0) {
        const viewportRatio = this.avgViewportH / this.avgViewportW
        // multiplica por ~2.2 para cobrir múltiplas dobras.
        ratio = Math.max(1.8, viewportRatio * 2.2)
      } else {
        // sem métricas: usa tela longa por padrão para não truncar.
        ratio = 2.6
      }

      // 3) Se o período indica rolagem profunda, amplia ainda mais.
      if (this.maxScrollPct >= 90) ratio = Math.max(ratio, 3.4)
      else if (this.maxScrollPct >= 70) ratio = Math.max(ratio, 2.9)
      else if (this.maxScrollPct >= 50) ratio = Math.max(ratio, 2.4)

      return Math.max(1000, Math.round(width * ratio))
    },

    _heatColor(t) {
      const stops = [[0,0,180],[0,180,255],[0,220,0],[255,220,0],[255,0,0]]
      const idx = t * (stops.length - 1)
      const lo = Math.floor(idx), hi = Math.min(stops.length - 1, lo + 1)
      const f = idx - lo
      return stops[lo].map((c, i) => Math.round(c + f * (stops[hi][i] - c)))
    },

    scrollBarColor(pct) {
      const [r, g, b] = this._heatColor(pct)
      return `rgb(${r},${g},${b})`
    },
  },
}
</script>

<style>
.hm-page {
  padding: 24px 20px 30px;
  min-height: 100vh;
  background: var(--dk-bg-page, #0D1117) !important;
  color: var(--dk-text-primary, #e2e8f0) !important;
  font-family: 'Inter', sans-serif;
  position: relative;
}

.hm-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
  padding: 18px 20px;
  background: var(--dk-bg-card, #111827) !important;
  border: 1px solid var(--dk-border, rgba(255,255,255,.07)) !important;
  border-radius: 14px;
  box-shadow: var(--dk-shadow-sm, 0 2px 8px rgba(0,0,0,.3));
}
.hm-title { font-size: 1.35rem; font-weight: 800; margin: 0 0 4px; color: var(--dk-text-primary, #e2e8f0) !important; }
.hm-subtitle { margin: 0; font-size: .86rem; color: var(--dk-text-muted, rgba(255,255,255,.52)) !important; max-width: 760px; }
.hm-date-group { display: flex; align-items: center; gap: 8px; font-size: .85rem; color: var(--dk-text-muted, rgba(255,255,255,.45)) !important; }
.hm-input-date {
  background: var(--dk-bg-input, #0f1724) !important;
  border: 1px solid var(--dk-border-input, rgba(255,255,255,.1)) !important;
  border-radius: 8px;
  color: var(--dk-text-primary, #e2e8f0) !important;
  padding: 6px 10px;
  font-size: .85rem;
  color-scheme: dark;
}
.hm-input-date::-webkit-calendar-picker-indicator {
  filter: invert(0.85) saturate(0);
  opacity: .8;
  cursor: pointer;
}
.hm-input-date:focus {
  outline: none;
  border-color: #E86A10;
  box-shadow: 0 0 0 3px rgba(232,106,16,.14);
}

.hm-vsls { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 16px; }
.hm-vsl-card {
  background: var(--dk-bg-card, #111827) !important;
  border: 1px solid var(--dk-border, rgba(255,255,255,.07)) !important;
  border-radius: 10px;
  padding: 12px 14px;
  cursor: pointer;
  transition: all .2s;
  min-width: 190px;
  box-shadow: var(--dk-shadow-sm, 0 2px 8px rgba(0,0,0,.3));
}
.hm-vsl-card:hover { border-color: rgba(232,106,16,.4); background: #16202f; }
.hm-vsl-card.active {
  border-color: #E86A10;
  background: linear-gradient(180deg, rgba(232,106,16,.18), rgba(23,33,48,.95));
  box-shadow: 0 0 0 2px rgba(232,106,16,.16);
}
.hm-vsl-card.active .hm-vsl-name {
  color: #f8fafc !important;
}
.hm-vsl-card.active .hm-badge.click {
  background: rgba(232,106,16,.24) !important;
  color: #ffd7b5 !important;
  border-color: rgba(232,106,16,.42) !important;
}
.hm-vsl-card.active .hm-badge.scroll {
  background: rgba(59,130,246,.22) !important;
  color: #cbe6ff !important;
  border-color: rgba(96,165,250,.38) !important;
}
.hm-vsl-name { font-size: .9rem; font-weight: 700; margin-bottom: 8px; color: var(--dk-text-primary, #e2e8f0) !important; }
.hm-vsl-stats { display: flex; gap: 6px; flex-wrap: wrap; }
.hm-badge { font-size: .7rem; padding: 3px 8px; border-radius: 20px; font-weight: 700; }
.hm-badge.click { background: rgba(232,106,16,.15); color: #E86A10; border: 1px solid rgba(232,106,16,.28); }
.hm-badge.scroll { background: rgba(0,180,255,.15); color: #5fd6ff; border: 1px solid rgba(0,180,255,.25); }
.hm-empty { color: var(--dk-text-muted, rgba(255,255,255,.45)) !important; font-size: .85rem; padding: 16px 0; text-align: center; width: 100%; }
.hm-empty p, .hm-empty small { font-size: .76rem; margin-top: 6px; display: block; }

.hm-main {
  background: var(--dk-bg-card, #111827) !important;
  border: 1px solid var(--dk-border, rgba(255,255,255,.07)) !important;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: var(--dk-shadow-md, 0 6px 24px rgba(0,0,0,.4));
}
.hm-tabs { display: flex; border-bottom: 1px solid var(--dk-border, rgba(255,255,255,.07)) !important; background: var(--dk-bg-inset, rgba(255,255,255,.04)) !important; padding: 0 8px; }
.hm-tab {
  display: flex; align-items: center; gap: 6px;
  padding: 13px 16px;
  font-size: .86rem; font-weight: 700;
  border: none; background: transparent;
  color: var(--dk-text-muted, rgba(255,255,255,.45)) !important;
  cursor: pointer; border-bottom: 2px solid transparent;
}
.hm-tab:hover { color: var(--dk-text-primary, #e2e8f0) !important; }
.hm-tab.active { color: #E86A10; border-bottom-color: #E86A10; }
.hm-tab-badge { font-size: 9px; font-weight: 800; padding: 2px 6px; border-radius: 4px; background: linear-gradient(135deg,#E86A10,#F5B935); color: #fff; }

.hm-canvas-wrap { padding: 18px; }
.hm-canvas-legend { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; font-size: .8rem; color: var(--dk-text-muted, rgba(255,255,255,.45)); }
.leg-bar { flex: 1; max-width: 200px; height: 10px; border-radius: 5px; background: linear-gradient(to right,#0000b4,#00b4ff,#00dc00,#ffdc00,#ff0000); }
.leg-total { margin-left: auto; color: var(--dk-text-primary, #e2e8f0); font-weight: 700; }

.hm-url-active { display: flex; align-items: center; gap: 6px; font-size: .75rem; color: #4fd18b; margin-bottom: 10px; padding: 5px 10px; background: rgba(16,185,129,.08); border: 1px solid rgba(16,185,129,.2); border-radius: 8px; }
.hm-url-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 480px; }
.hm-url-missing { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; font-size: .78rem; color: var(--dk-text-muted, rgba(255,255,255,.45)); margin-bottom: 10px; padding: 7px 12px; background: rgba(255,255,255,.03); border: 1px solid var(--dk-border, rgba(255,255,255,.07)); border-radius: 8px; }
.hm-url-link { color: #E86A10; text-decoration: none; font-weight: 700; margin-left: 4px; }
.hm-url-link:hover { text-decoration: underline; }
.hm-iframe-warn { font-size: .78rem; color: #f59e0b; margin-bottom: 8px; padding: 6px 10px; background: rgba(245,158,11,.08); border: 1px solid rgba(245,158,11,.2); border-radius: 8px; }

.hm-canvas-container {
  position: relative; width: 100%; max-width: 860px;
  margin: 0 auto; border-radius: 12px; overflow: hidden;
  background: #0f1724;
  border: 1px solid var(--dk-border, rgba(255,255,255,.08));
  box-shadow: 0 4px 18px rgba(0,0,0,.25);
}
.hm-canvas-container.hm-has-bg { background: #fff; }
.hm-bg-iframe { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none; pointer-events: none; z-index: 0; }
.hm-canvas { display: block; width: 100%; height: auto; position: relative; z-index: 1; }
.hm-canvas-empty, .hm-canvas-loading { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; color: var(--dk-text-muted, rgba(255,255,255,.45)); font-size: .9rem; background: rgba(13,17,23,.86); }
.hm-canvas-hint { text-align: center; font-size: .78rem; color: var(--dk-text-muted, rgba(255,255,255,.4)); margin-top: 10px; }

.hm-scroll-wrap { padding: 20px; }
.hm-scroll-header { font-size: .84rem; color: var(--dk-text-muted, rgba(255,255,255,.45)) !important; margin-bottom: 16px; }
.hm-scroll-chart {
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: var(--dk-bg-inset, rgba(255,255,255,.04)) !important;
  border: 1px solid var(--dk-border-light, rgba(255,255,255,.05)) !important;
  border-radius: 12px;
  padding: 12px;
}
.hm-scroll-row { display: flex; align-items: center; gap: 12px; }
.hm-scroll-label { width: 44px; text-align: right; font-size: .8rem; color: var(--dk-text-muted, rgba(255,255,255,.45)) !important; flex-shrink: 0; }
.hm-scroll-bar-wrap {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--dk-bg-segment, rgba(255,255,255,.06)) !important;
  border: 1px solid var(--dk-border-light, rgba(255,255,255,.05)) !important;
  border-radius: 7px;
  padding: 3px 6px;
}
.hm-scroll-bar { height: 22px; border-radius: 4px; min-width: 4px; transition: width .4s ease; }
.hm-scroll-count { font-size: .78rem; color: var(--dk-text-secondary, rgba(255,255,255,.72)) !important; white-space: nowrap; }

.hm-beh-wrap { padding: 20px; display: flex; flex-direction: column; gap: 22px; }
.hm-beh-section-title { font-size: .95rem; font-weight: 700; color: var(--dk-text-primary, #e2e8f0); margin: 0 0 4px; }
.hm-beh-section-sub { font-size: .8rem; color: var(--dk-text-muted, rgba(255,255,255,.45)); margin: 0 0 12px; }
.hm-beh-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(210px, 1fr)); gap: 12px; margin-top: 12px; }
.hm-beh-card { background: var(--dk-bg-card, #111827); border: 1px solid var(--dk-border, rgba(255,255,255,.07)); border-radius: 12px; padding: 16px; display: flex; align-items: flex-start; gap: 12px; box-shadow: 0 2px 10px rgba(0,0,0,.18); }
.hm-beh-card-icon { width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 17px; flex-shrink: 0; }
.hm-beh-card-value { font-size: 1.2rem; font-weight: 800; color: var(--dk-text-primary, #e2e8f0); line-height: 1.1; }
.hm-beh-card-label { font-size: .76rem; font-weight: 700; color: var(--dk-text-secondary, rgba(255,255,255,.72)); margin-top: 2px; }
.hm-beh-card-desc { font-size: .7rem; color: var(--dk-text-muted, rgba(255,255,255,.4)); margin-top: 4px; }

.hm-clusters { display: flex; flex-direction: column; gap: 10px; margin-top: 14px; }
.hm-cluster-row { display: flex; align-items: center; gap: 10px; }
.hm-cluster-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.hm-cluster-name { width: 110px; font-size: .82rem; font-weight: 700; color: var(--dk-text-secondary, rgba(255,255,255,.72)); flex-shrink: 0; }
.hm-cluster-bar-wrap { flex: 1; background: rgba(255,255,255,.06); border-radius: 6px; height: 22px; overflow: hidden; }
.hm-cluster-bar { height: 100%; border-radius: 6px; min-width: 4px; transition: width .4s ease; opacity: .9; }
.hm-cluster-pct { width: 40px; text-align: right; font-size: .8rem; font-weight: 700; color: var(--dk-text-primary, #e2e8f0); flex-shrink: 0; }
.hm-cluster-n { width: 60px; text-align: right; font-size: .72rem; color: var(--dk-text-muted, rgba(255,255,255,.45)); flex-shrink: 0; }

.hm-cluster-defs { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 12px; margin-top: 10px; }
.hm-cluster-def { background: var(--dk-bg-card, #111827); border: 1px solid var(--dk-border, rgba(255,255,255,.07)); border-radius: 10px; padding: 12px 14px; }
.hm-cluster-def-badge { display: inline-block; font-size: .72rem; font-weight: 700; padding: 3px 9px; border-radius: 6px; border: 1px solid; margin-bottom: 8px; }
.hm-cluster-def-text { font-size: .78rem; color: var(--dk-text-muted, rgba(255,255,255,.45)); line-height: 1.45; }

.hm-insights { display: flex; flex-direction: column; gap: 10px; margin-top: 10px; }
.hm-insight { display: flex; align-items: flex-start; gap: 10px; padding: 12px 14px; border-radius: 10px; border: 1px solid; }
.hm-insight.ok { background: rgba(16,185,129,.08); border-color: rgba(16,185,129,.25); }
.hm-insight.warn { background: rgba(239,68,68,.08); border-color: rgba(239,68,68,.25); }
.hm-insight.info { background: rgba(59,130,246,.08); border-color: rgba(59,130,246,.25); }
.hm-insight-icon { font-size: 1rem; flex-shrink: 0; margin-top: 1px; }
.hm-insight-text { font-size: .82rem; color: var(--dk-text-secondary, rgba(255,255,255,.75)); line-height: 1.45; }

.hm-ai-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.hm-ai-btn {
  display: flex; align-items: center; gap: 6px;
  padding: 7px 12px; border-radius: 8px; border: 1px solid rgba(232,106,16,.3);
  cursor: pointer; font-size: .78rem; font-weight: 700;
  background: linear-gradient(135deg, rgba(232,106,16,.2), rgba(245,185,53,.15));
  color: #F5B935;
}
.hm-ai-btn:disabled { opacity: .6; cursor: default; }
.hm-ai-btn:not(:disabled):hover { background: linear-gradient(135deg, rgba(232,106,16,.32), rgba(245,185,53,.24)); }
.hm-ai-spin { width: 12px; height: 12px; border-radius: 50%; border: 2px solid rgba(245,185,53,.3); border-top-color: #F5B935; animation: spin .7s linear infinite; }
.hm-ai-result { background: rgba(245,185,53,.06); border: 1px solid rgba(245,185,53,.18); border-radius: 10px; padding: 14px 16px; font-size: .82rem; color: var(--dk-text-secondary, rgba(255,255,255,.78)); line-height: 1.65; white-space: pre-wrap; }
.hm-ai-placeholder { font-size: .8rem; color: var(--dk-text-muted, rgba(255,255,255,.45)); padding: 10px 0; font-style: italic; }

.hm-loading-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.4); display: flex; align-items: center; justify-content: center; z-index: 999; }
.hm-spinner { width: 40px; height: 40px; border: 3px solid rgba(255,255,255,.1); border-top-color: #E86A10; border-radius: 50%; animation: spin .7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 900px) {
  .hm-page { padding: 14px 10px 20px; }
  .hm-header { padding: 14px; }
  .hm-title { font-size: 1.15rem; }
  .hm-tab { padding: 11px 12px; font-size: .78rem; }
}

/* ── Dashboard identity hard-alignment ─────────────────────────── */
.layout-app__content .hm-page {
  background: #0D1117 !important;
  color: #e2e8f0 !important;
}

.layout-app__content .hm-header,
.layout-app__content .hm-main,
.layout-app__content .hm-beh-card,
.layout-app__content .hm-cluster-def,
.layout-app__content .hm-vsl-card {
  background: #111827 !important;
  border-color: rgba(255,255,255,.07) !important;
  box-shadow: 0 2px 12px rgba(0,0,0,.28) !important;
}

.layout-app__content .hm-tabs {
  background: rgba(255,255,255,.02) !important;
  border-bottom-color: rgba(255,255,255,.07) !important;
}

.layout-app__content .hm-tab {
  color: rgba(255,255,255,.45) !important;
}

.layout-app__content .hm-tab:hover {
  color: #e2e8f0 !important;
}

.layout-app__content .hm-tab.active {
  color: #E86A10 !important;
  border-bottom-color: #E86A10 !important;
}

.layout-app__content .hm-title,
.layout-app__content .hm-vsl-name,
.layout-app__content .hm-beh-section-title,
.layout-app__content .hm-beh-card-value,
.layout-app__content .hm-cluster-pct {
  color: #e2e8f0 !important;
}

.layout-app__content .hm-subtitle,
.layout-app__content .hm-beh-section-sub,
.layout-app__content .hm-beh-card-desc,
.layout-app__content .hm-cluster-def-text,
.layout-app__content .hm-scroll-header,
.layout-app__content .hm-scroll-label,
.layout-app__content .hm-scroll-count,
.layout-app__content .hm-empty,
.layout-app__content .hm-ai-placeholder {
  color: rgba(255,255,255,.45) !important;
}

.layout-app__content .hm-date-group {
  color: rgba(255,255,255,.45) !important;
}

.layout-app__content .hm-input-date {
  background: #0f1724 !important;
  color: #e2e8f0 !important;
  border-color: rgba(255,255,255,.1) !important;
}

.layout-app__content .hm-input-date:focus {
  border-color: #E86A10 !important;
  box-shadow: 0 0 0 3px rgba(232,106,16,.14) !important;
}

.layout-app__content .hm-vsl-card:hover {
  background: #16202f !important;
  border-color: rgba(232,106,16,.35) !important;
}

.layout-app__content .hm-vsl-card.active {
  background: linear-gradient(180deg, rgba(232,106,16,.18), rgba(23,33,48,.95)) !important;
  border-color: #E86A10 !important;
  box-shadow: 0 0 0 2px rgba(232,106,16,.12) !important;
}

.layout-app__content .hm-canvas-container {
  background: #0f1724 !important;
  border-color: rgba(255,255,255,.08) !important;
}

.layout-app__content .hm-canvas-empty,
.layout-app__content .hm-canvas-loading {
  background: rgba(13,17,23,.88) !important;
  color: rgba(255,255,255,.52) !important;
}

.layout-app__content .hm-scroll-bar-wrap,
.layout-app__content .hm-cluster-bar-wrap {
  background: rgba(255,255,255,.05) !important;
}

.layout-app__content .hm-insight-text {
  color: rgba(255,255,255,.74) !important;
}

.layout-app__content .hm-ai-result {
  background: rgba(245,185,53,.07) !important;
  border-color: rgba(245,185,53,.2) !important;
  color: rgba(255,255,255,.78) !important;
}

/* ── Force dashboard dark identity even without .dark-layout ── */
body:not(.dark-layout) .hm-page {
  background: #0D1117 !important;
  color: #e2e8f0 !important;
}
body:not(.dark-layout) .hm-header,
body:not(.dark-layout) .hm-main,
body:not(.dark-layout) .hm-vsl-card,
body:not(.dark-layout) .hm-beh-card,
body:not(.dark-layout) .hm-cluster-def {
  background: #111827 !important;
  border-color: rgba(255,255,255,.07) !important;
  box-shadow: 0 2px 12px rgba(0,0,0,.28) !important;
}
body:not(.dark-layout) .hm-title,
body:not(.dark-layout) .hm-vsl-name,
body:not(.dark-layout) .hm-beh-section-title,
body:not(.dark-layout) .hm-beh-card-value,
body:not(.dark-layout) .hm-cluster-pct {
  color: #e2e8f0 !important;
}
body:not(.dark-layout) .hm-subtitle,
body:not(.dark-layout) .hm-date-group,
body:not(.dark-layout) .hm-empty,
body:not(.dark-layout) .hm-scroll-header,
body:not(.dark-layout) .hm-scroll-label,
body:not(.dark-layout) .hm-scroll-count,
body:not(.dark-layout) .hm-beh-section-sub,
body:not(.dark-layout) .hm-cluster-def-text,
body:not(.dark-layout) .hm-ai-placeholder {
  color: rgba(255,255,255,.45) !important;
}
body:not(.dark-layout) .hm-tabs {
  background: rgba(255,255,255,.02) !important;
  border-bottom-color: rgba(255,255,255,.07) !important;
}
body:not(.dark-layout) .hm-tab {
  color: rgba(255,255,255,.45) !important;
}
body:not(.dark-layout) .hm-tab:hover {
  color: #e2e8f0 !important;
}
body:not(.dark-layout) .hm-tab.active {
  color: #E86A10 !important;
  border-bottom-color: #E86A10 !important;
}
body:not(.dark-layout) .hm-input-date {
  background: #0f1724 !important;
  color: #e2e8f0 !important;
  border-color: rgba(255,255,255,.1) !important;
}
body:not(.dark-layout) .hm-scroll-chart {
  background: rgba(255,255,255,.04) !important;
  border-color: rgba(255,255,255,.05) !important;
}
body:not(.dark-layout) .hm-scroll-bar-wrap,
body:not(.dark-layout) .hm-cluster-bar-wrap {
  background: rgba(255,255,255,.05) !important;
  border-color: rgba(255,255,255,.05) !important;
}
body:not(.dark-layout) .hm-vsl-card.active {
  background: linear-gradient(180deg, rgba(232,106,16,.18), rgba(23,33,48,.95)) !important;
}
body:not(.dark-layout) .hm-vsl-card.active .hm-vsl-name {
  color: #f8fafc !important;
}
body:not(.dark-layout) .hm-vsl-card.active .hm-badge.click {
  background: rgba(232,106,16,.24) !important;
  color: #ffd7b5 !important;
  border-color: rgba(232,106,16,.42) !important;
}
body:not(.dark-layout) .hm-vsl-card.active .hm-badge.scroll {
  background: rgba(59,130,246,.22) !important;
  color: #cbe6ff !important;
  border-color: rgba(96,165,250,.38) !important;
}
</style>
